/* ═══════════════════════════════════════════════════════════════════════════
   Is this tab still running the deployed build?
   ───────────────────────────────────────────────────────────────────────────
   Copied verbatim between the three front ends, like lib/telemetry.ts. It
   reads `config` for the one value that differs.

   ── The problem ─────────────────────────────────────────────────────────────

   A deploy replaces index.html and the hashed assets it points at. A browser
   already holding the old index.html goes on running the old build, and every
   fix in the new one is invisible to it. There are three ways to end up there
   and all three happen daily:

     · the tab has simply been open since before the deploy, so nothing has
       re-read index.html;
     · a service worker is answering navigations from its own precache, which
       is before the network — so no cache header can reach it;
     · the HTTP cache held index.html, which `no-cache` in .htaccess prevents
       but only on a server that is answering at all.

   There is no event for "the server changed". The only way a tab finds out is
   by asking, and nothing here was asking — which is why the fix has been to
   clear the browser cache by hand.

   ── The answer ──────────────────────────────────────────────────────────────

   `vite.config.ts` mints one id per build and puts it in two places: inlined
   into the bundle as `__BUILD_ID__`, and written to `dist/version.json`. They
   agree by construction at build time, and disagree exactly when the running
   tab is behind the server. So the check is one small GET and a string
   comparison — no worker, no headers to get right, and it behaves identically
   in the app that has a service worker and the one that does not.

   Detecting is kept apart from acting on it: this module only reports, the
   dialog asks, and `applyNewVersion` is what runs when the answer is yes.
   ═══════════════════════════════════════════════════════════════════════════ */

import { config } from './config'

/** Minted per build by vite.config.ts. `dev` when the define is absent, which
 *  is only ever a test run — the watch below does not start in development. */
const RUNNING_BUILD = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'

/** What `dist/version.json` holds. */
export interface DeployedBuild {
  /** The build the server is currently serving. */
  buildId: string
  /** The human-readable one, for the report a person files afterwards. */
  version: string
}

/* Beside index.html, and reached the same way index.html is — through
   BASE_URL rather than a hard-coded '/'. */
const MANIFEST_URL = `${import.meta.env.BASE_URL}version.json`

/** How often a tab nobody touches re-asks.
 *
 *  Half an hour, and it is only the backstop: the check that actually catches
 *  things is the one on the next line, when the tab comes back to the
 *  foreground. A tab genuinely left alone for hours is not one where being
 *  half an hour late matters. */
const POLL_MS = 30 * 60 * 1000

/** How long after start the first check waits.
 *
 *  Not immediate. On a first visit this would compete with the route chunk for
 *  the same connection, and the answer is almost never interesting on a load
 *  that just came from the network. Two seconds is comfortably past first
 *  paint and still inside the time it takes to read the screen. */
const FIRST_CHECK_MS = 2000

type Listener = (build: DeployedBuild) => void

const listeners = new Set<Listener>()

/** The newer build, once one has been seen.
 *
 *  Latched deliberately. A tab that has been told it is stale does not become
 *  fresh again, and asking again after that point can only produce the same
 *  answer at the cost of a request. */
let staleAgainst: DeployedBuild | null = null

/** One check at a time. The interval and a tab regaining focus can land
 *  together, and two identical GETs answer one question. */
let inFlight = false

/** Called with the deployed build the moment this tab is found to be behind
 *  it. Returns the unsubscribe.
 */
export function onNewVersion(fn: Listener): () => void {
  listeners.add(fn)
  /* A late subscriber still hears about a check that landed before it mounted.
     Cheap insurance rather than a known case: the dialog mounts with the app,
     ahead of the first check. */
  if (staleAgainst !== null) fn(staleAgainst)
  return () => {
    listeners.delete(fn)
  }
}

/** Reads the manifest, or answers null.
 *
 *  Every failure is null, and null means "carry on" rather than "assume
 *  stale". That direction is the whole safety of this module: a bad connection,
 *  a moment of 502, an SPA fallback serving index.html where the manifest
 *  should be — none of those mean a new version exists, and prompting on them
 *  would show a reload dialog to somebody it cannot help, over and over.
 */
async function readDeployedBuild(): Promise<DeployedBuild | null> {
  /* `no-store` *and* a cache-buster. The header is the correct instruction and
     browsers honour it; the query string is what keeps the answer honest
     through an intermediary that does not. This is the one request in the app
     whose entire value is that it was not served from a cache. */
  const url = `${MANIFEST_URL}?t=${Date.now()}`

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return null

    const body: unknown = await response.json()
    if (typeof body !== 'object' || body === null) return null

    const { buildId, version } = body as Partial<DeployedBuild>
    if (typeof buildId !== 'string' || buildId === '') return null

    return { buildId, version: typeof version === 'string' ? version : '' }
  } catch {
    /* Offline, aborted, or the SPA fallback answered with HTML that will not
       parse as JSON. All of them are "ask again later". */
    return null
  }
}

/** One check. True when the deployed build is not the one running here.
 *
 *  Exported so a caller can ask on demand, and so the behaviour above can be
 *  tested without waiting on the watch's timers.
 */
export async function checkForNewVersion(): Promise<boolean> {
  if (staleAgainst !== null) return true
  if (inFlight) return false
  /* An offline check is a guaranteed rejection. Skipping it is not an
     optimisation — it keeps a failed request out of the console every half
     hour for somebody on a train. */
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false

  inFlight = true
  try {
    const deployed = await readDeployedBuild()
    if (deployed === null) return false
    /* `!==` rather than "newer than". A rollback is a deploy too, and a tab
       running the build that was just withdrawn is exactly as wrong as one
       running a build that has been superseded. */
    if (deployed.buildId === RUNNING_BUILD) return false

    staleAgainst = deployed
    for (const fn of listeners) fn(deployed)
    return true
  } finally {
    inFlight = false
  }
}

/** Starts asking. Returns the stop.
 *
 *  Owned by the dialog rather than by startup, so the thing that reacts to the
 *  answer is the thing that asked for it.
 */
export function startVersionWatch(): () => void {
  /* Nothing to compare against in development: Vite serves modules from memory
     and the manifest is a build artifact. HMR is the update mechanism there,
     and it is a better one. */
  if (config.isDev) return () => {}

  let timer: ReturnType<typeof setInterval> | null = null
  const check = () => {
    void checkForNewVersion()
  }

  const first = setTimeout(() => {
    check()
    timer = setInterval(check, POLL_MS)
  }, FIRST_CHECK_MS)

  /* The check that earns its keep. A phone unlocking onto a backgrounded tab,
     or somebody coming back to a screen they left open at lunch, is the single
     most likely moment for a deploy to have happened since this tab last
     looked. */
  const onVisible = () => {
    if (document.visibilityState === 'visible') check()
  }
  document.addEventListener('visibilitychange', onVisible)

  return () => {
    clearTimeout(first)
    if (timer !== null) clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  }
}

/** The script URL of whichever worker a registration currently has. */
function scriptUrlOf(registration: ServiceWorkerRegistration): string {
  const worker = registration.active ?? registration.waiting ?? registration.installing
  return worker?.scriptURL ?? ''
}

/** Everything this origin is holding that could still answer with the old
 *  build.
 *
 *  Separate from `applyNewVersion` below so it can be tested without a
 *  navigation, which jsdom does not have.
 */
export async function clearCachedBuild(): Promise<void> {
  await dropServiceWorkers()
  await dropCaches()
}

async function dropServiceWorkers(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        /* The push worker is not ours to drop. Unregistering it throws away the
           FCM subscription with it, and the partner app re-creates that only on
           a sign-in — so somebody who pressed OK here would quietly stop
           getting job alerts with no way to know why. It precaches nothing and
           serves no navigation, so it is not part of the problem being solved.

           Named rather than filtered by scope: both workers are registered at
           the root. */
        .filter((registration) => !scriptUrlOf(registration).includes('firebase-messaging-sw'))
        .map((registration) => registration.unregister()),
    )
  } catch {
    /* A worker that will not unregister is not a reason to skip the reload —
       the reload is what the user asked for, and dropping the caches below
       gets most of the way there on its own. */
  }
}

async function dropCaches(): Promise<void> {
  if (typeof caches === 'undefined') return

  try {
    const names = await caches.keys()
    await Promise.all(names.map((name) => caches.delete(name)))
  } catch {
    /* Same reasoning as above: reload regardless. */
  }
}

/** Clears the stored copy of the app and reloads onto the deployed one.
 *
 *  All of it, rather than a careful subset. The runtime caches hold responses
 *  keyed by URL, and the URLs that are not content-hashed — a photograph in
 *  public/, the manifest, index.html itself — are exactly the ones a deploy can
 *  change without renaming. Keeping any of those would leave a way for this
 *  button to be pressed and change nothing, which is the failure it exists to
 *  end.
 *
 *  What is deliberately *not* touched: localStorage and sessionStorage. The
 *  basket, the queued presses waiting for signal, the session — all of that
 *  lives there, and none of it is what went stale.
 */
export async function applyNewVersion(): Promise<void> {
  await clearCachedBuild()
  window.location.reload()
}

/** Test seam. The latch and the in-flight guard are module state by design —
 *  they outlive every component that reads them — which makes clearing them
 *  between cases this module's job rather than a test's.
 */
export function __resetVersionState(): void {
  staleAgainst = null
  inFlight = false
  listeners.clear()
}
